import { DataTable, DataTableColumn, DataTableFilter } from "@/components/DataTable";
import { ApiRequestParams, ApiResponse } from "@/components/DataTable/types";
import { ActionMenu } from "@/components/admin";
import {
  ApprovalStats,
  FinanceApprovalItem,
  FinanceProvider,
  GetApprovalsParams,
  approveInvoice,
  getApprovalStats,
  getApprovalsForAssignedCompanies,
  rejectInvoice,
} from "@/services/financeService";
import { useUserStore } from "@/stores/userStore";
import { formatCompanyDate } from "@/utils/dateUtils";
import { Button, Card, Input, Modal, Tag, message } from "antd";
import { CheckCircle, Clock, FileText, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import ApprovalConfirmationModal from "./ApprovalConfirmationModal";
import ViewApprovalModal from "./ViewApprovalModal";

const { TextArea } = Input;

interface ApprovalManagementProps {
  /**
   * The provider (quickbooks or netsuite)
   * Determines which API to call and data format to use
   */
  provider: FinanceProvider;
}

/**
 * ApprovalManagement component for Finance module
 * Displays invoice approvals across all assigned companies
 * Finance users can view, approve, and reject invoices from multiple companies
 * Uses server-side pagination and filtering
 */
const ApprovalManagement = ({ provider }: ApprovalManagementProps) => {
  const dateFormat = useUserStore((state) => state.currentCompany?.dateFormat);
  const [selectedApproval, setSelectedApproval] = useState<FinanceApprovalItem | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const { user, currentCompany } = useUserStore();

  // Stats state
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  // Company options for filter dropdown
  const [companyOptions, setCompanyOptions] = useState<Array<{ label: string; value: string }>>([]);
  // Refresh key to trigger DataTable reload
  const [refreshKey, setRefreshKey] = useState(0);

  // Approve modal state
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approvingApproval, setApprovingApproval] = useState<FinanceApprovalItem | null>(null);
  const [approving, setApproving] = useState(false);

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingApproval, setRejectingApproval] = useState<FinanceApprovalItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const providerTitle = provider === "quickbooks" ? "QuickBooks" : "NetSuite";

  /**
   * Fetch stats once and memoize (unfiltered aggregates)
   */
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const response = await getApprovalStats(
          provider,
          user?.role === "Admin" ? currentCompany?.id : undefined // For Admin users, filter by current company only. Finance users see all companies.
        );
        setStats(response);
        if (response.companies && response.companies.length > 0) {
          setCompanyOptions(response.companies.map((c) => ({ label: c.name, value: c.id })));
        }
      } catch (error) {
        console.error("Failed to fetch approval stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [provider]);

  /**
   * Custom fetch function for DataTable - server-side pagination
   */
  const fetchApprovals = useCallback(
    async (params: ApiRequestParams): Promise<ApiResponse<FinanceApprovalItem>> => {
      try {
        const apiParams: GetApprovalsParams = {
          page: params.page || 1,
          pageSize: params.pageSize || 10,
          search: params.search,
          companyId: user?.role === "Admin" ? currentCompany?.id : undefined, // For Admin users, filter by current company only. Finance users see all companies.
        };

        if (params.filters) {
          if (params.filters.companyId) {
            apiParams.companyId = params.filters.companyId as string;
          }
          if (params.filters.status) {
            apiParams.status = params.filters.status as "Pending" | "Approved" | "Rejected" | "Auto-Approved";
          }
          if (params.filters.priority) {
            apiParams.priority = params.filters.priority as "High" | "Medium" | "Low";
          }
          if (params.filters.isRecurring !== undefined) {
            apiParams.isRecurring = String(params.filters.isRecurring) as "true" | "false";
          }
          if (params.filters.submittedDate) {
            const dateRange = params.filters.submittedDate as unknown as string[];
            if (Array.isArray(dateRange) && dateRange.length === 2) {
              apiParams.submittedDateStart = dateRange[0];
              apiParams.submittedDateEnd = dateRange[1];
            }
          }
          if (params.filters.amount) {
            const amountRange = params.filters.amount as unknown as [number | undefined, number | undefined];
            if (Array.isArray(amountRange)) {
              if (amountRange[0] !== undefined) apiParams.amountMin = amountRange[0];
              if (amountRange[1] !== undefined) apiParams.amountMax = amountRange[1];
            }
          }
        }

        const response = await getApprovalsForAssignedCompanies(provider, apiParams);

        return {
          data: response.data,
          pagination: {
            total: response.pagination.total,
            page: response.pagination.page,
            pageSize: response.pagination.pageSize,
            totalPages: response.pagination.totalPages,
          },
        };
      } catch (error) {
        console.error("Failed to fetch approvals:", error);
        message.error("Failed to load approvals");
        return { data: [], pagination: { total: 0, page: 1, pageSize: 10 } };
      }
    },
    [provider],
  );

  /**
   * Refresh table data and stats
   */
  const handleRefresh = async () => {
    setRefreshKey((prev) => prev + 1);
    try {
      const response = await getApprovalStats(provider);
      setStats(response);
    } catch (error) {
      console.error("Failed to refresh stats:", error);
    }
  };
  /**
   * Open approve modal
   */
  const handleOpenApproveModal = (approval: FinanceApprovalItem) => {
    setApprovingApproval(approval);
    setApproveModalOpen(true);
  };

  /**
   * Handle confirm approve invoice
   */
  const handleConfirmApprove = async () => {
    if (!approvingApproval) return;

    setApproving(true);
    try {
      await approveInvoice(approvingApproval.id);
      message.success("Invoice approved successfully");
      setApproveModalOpen(false);
      setApprovingApproval(null);
      handleRefresh();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || "Failed to approve invoice");
    } finally {
      setApproving(false);
    }
  };

  /**
   * Open reject modal (only available for QuickBooks)
   */
  /**
   * Open reject modal
   */
  const handleOpenRejectModal = (approval: FinanceApprovalItem) => {
    setRejectingApproval(approval);
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  /**
   * Handle reject invoice
   */
  const handleReject = async () => {
    if (!rejectingApproval || !rejectionReason.trim()) {
      message.warning("Please provide a rejection reason");
      return;
    }

    setRejecting(true);
    try {
      await rejectInvoice(rejectingApproval.id, rejectionReason.trim());
      message.success("Invoice rejected successfully");
      setRejectModalOpen(false);
      setRejectingApproval(null);
      setRejectionReason("");
      handleRefresh();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || "Failed to reject invoice");
    } finally {
      setRejecting(false);
    }
  };

  /**
   * Define columns for DataTable - sortable removed per requirements
   */
  const columns: DataTableColumn<FinanceApprovalItem>[] = [
    {
      key: "companyName",
      title: "Company",
      dataIndex: "companyName",
      render: (value: unknown) => <span className="text-gray-700 font-medium">{(value as string) || "—"}</span>,
    },
    {
      key: "clientName",
      title: "Client",
      dataIndex: "clientName",
      render: (value: unknown) => <span className="font-semibold text-gray-900">{(value as string) || "—"}</span>,
    },
    {
      key: "amount",
      title: "Amount",
      dataIndex: "amount",
      render: (value: unknown) => (
        <span className="font-bold text-gray-900">${Number(value || 0).toLocaleString()}</span>
      ),
    },
    ...(provider === "quickbooks"
      ? [
          {
            key: "isRecurring",
            title: "Type",
            dataIndex: "isRecurring",
            render: (value: unknown) => {
              const isRecurring = Boolean(value);
              return <Tag color={isRecurring ? "blue" : "default"}>{isRecurring ? "Recurring" : "One-Time"}</Tag>;
            },
          } as DataTableColumn<FinanceApprovalItem>,
        ]
      : []),
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (val: unknown) => {
        const status = String(val || "Unknown");
        const colorMap: Record<string, string> = {
          Pending: "warning",
          Approved: "success",
          "Auto-Approved": "success",
          Rejected: "error",
        };
        return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
      },
    },
    {
      key: "rejectionReason",
      title: "Rejection Reason",
      dataIndex: "rejectionReason",
      render: (value: unknown, record: FinanceApprovalItem) => (
        <span className="text-gray-500 italic max-w-xs truncate block" title={value as string}>
          {record.status === "Rejected" ? (value as string) || "N/A" : "-"}
        </span>
      ),
    },
    {
      key: "submittedBy",
      title: "Submitted By",
      dataIndex: "submittedBy",
      render: (value: unknown) => <span className="text-gray-600">{(value as string) || "—"}</span>,
    },
    {
      key: "submittedDate",
      title: "Submitted",
      dataIndex: "submittedDate",
      render: (value: unknown) => (value ? formatCompanyDate(value as string, dateFormat) : "—"),
    },
    {
      key: "priority",
      title: "Priority",
      dataIndex: "priority",
      render: (val: unknown) => {
        const priority = String(val || "Medium");
        const colorMap: Record<string, string> = {
          High: "red",
          Medium: "blue",
          Low: "default",
        };
        return <Tag color={colorMap[priority] || "default"}>{priority}</Tag>;
      },
    },
  ];

  /**
   * Define filters for DataTable - includes date and amount range
   */
  const filters: DataTableFilter[] = [
    {
      label: "Company",
      key: "companyId",
      type: "select",
      placeholder: "All Companies",
      options: companyOptions,
    },
    {
      label: "Status",
      key: "status",
      type: "select",
      placeholder: "All Status",
      options: [
        { label: "Pending", value: "Pending" },
        { label: "Approved", value: "Approved" },
        { label: "Auto-Approved", value: "Auto-Approved" },
        { label: "Rejected", value: "Rejected" },
      ],
    },
    {
      label: "Priority",
      key: "priority",
      type: "select",
      placeholder: "All Priorities",
      options: [
        { label: "High", value: "High" },
        { label: "Medium", value: "Medium" },
        { label: "Low", value: "Low" },
      ],
    },
    ...(provider === "quickbooks"
      ? [
          {
            label: "Recurring",
            key: "isRecurring",
            type: "select",
            placeholder: "All Types",
            options: [
              { label: "Recurring", value: "true" },
              { label: "One-Time", value: "false" },
            ],
          } as DataTableFilter,
        ]
      : []),
    {
      label: "Submitted Date Range",
      key: "submittedDate",
      type: "dateRange",
      placeholder: "Filter by submitted date",
    },
    {
      label: "Amount",
      key: "amount",
      type: "numberRange",
      prefix: "$",
      minPlaceholder: "Min",
      maxPlaceholder: "Max",
    },
  ];

  /**
   * Opens approval details modal
   */
  const handleViewDetails = (approval: FinanceApprovalItem) => {
    setSelectedApproval(approval);
    setDetailsModalOpen(true);
  };

  /**
   * Handle action menu selection
   */
  const handleAction = (key: string, record: FinanceApprovalItem) => {
    switch (key) {
      case "view":
        handleViewDetails(record);
        break;
      case "approve":
        handleOpenApproveModal(record);
        break;
      case "reject":
        handleOpenRejectModal(record);
        break;
    }
  };

  /**
   * Custom action column renderer with dropdown menu
   */
  const actionColumn = (record: FinanceApprovalItem) => {
    const items = [{ key: "view", label: "View Details" }];

    // Show approve/reject for pending invoices for both providers
    if (record.status === "Pending") {
      items.unshift({ key: "approve", label: "Approve" }, { key: "reject", label: "Reject" });
    }

    return <ActionMenu items={items} onSelect={(key) => handleAction(key, record)} />;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{user?.role === "Finance" ? providerTitle : ""} Invoice Approvals</h1>
          <p className="text-gray-600 mt-1">
            { user?.role === "Finance" ? `Review and manage ${providerTitle} invoice approvals across all your assigned companies` : "Review and manage invoice approvals"}
          </p>
        </div>
        <Button type="primary" onClick={handleRefresh}>
          Refresh
        </Button>
      </div>

      {/* Statistics Cards - Memoized stats (unfiltered totals) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-l-4 border-l-blue-500" loading={statsLoading}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Approvals</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalApprovals || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <FileText className="text-blue-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="border-l-4 border-l-green-500" loading={statsLoading}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats?.approvedCount || 0}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="border-l-4 border-l-yellow-500" loading={statsLoading}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.pendingCount || 0}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <Clock className="text-yellow-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="border-l-4 border-l-red-500" loading={statsLoading}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{stats?.rejectedCount || 0}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <XCircle className="text-red-600" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Approvals Table Card - Server-side pagination */}
      <Card className="shadow-sm">
        <DataTable<FinanceApprovalItem>
          columns={columns}
          fetchData={fetchApprovals}
          searchable
          searchPlaceholder="Search approvals..."
          filters={filters}
          actionColumn={actionColumn}
          actionColumnTitle="Actions"
          exportOptions={{
            csv: true,
            excel: true,
            filename: "invoice-approvals-export",
          }}
          pagination
          defaultPageSize={10}
          rowKey={(record) => `${record.companyId}-${record.id}`}
          emptyText="No approvals found for your assigned companies."
          refreshKey={refreshKey}
        />
      </Card>

      {/* Approval Details Modal */}
      <ViewApprovalModal
        open={detailsModalOpen}
        approval={selectedApproval}
        dateFormat={dateFormat}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedApproval(null);
        }}
      />

      {/* Approval Confirmation Modal */}
      <ApprovalConfirmationModal
        open={approveModalOpen}
        approval={approvingApproval}
        loading={approving}
        onConfirm={handleConfirmApprove}
        onCancel={() => {
          setApproveModalOpen(false);
          setApprovingApproval(null);
        }}
      />

      {/* Rejection Reason Modal */}
      <Modal
        title="Reject Invoice"
        open={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectingApproval(null);
          setRejectionReason("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setRejectModalOpen(false);
              setRejectingApproval(null);
              setRejectionReason("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="reject"
            type="primary"
            danger
            loading={rejecting}
            disabled={!rejectionReason.trim()}
            onClick={handleReject}
          >
            Reject Invoice
          </Button>,
        ]}
        destroyOnClose
      >
        {rejectingApproval && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="font-medium">{rejectingApproval.companyName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium">{rejectingApproval.clientName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="font-bold text-lg">${Number(rejectingApproval.amount).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Submitted By</p>
                  <p className="font-medium">{rejectingApproval.submittedBy}</p>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <TextArea
                rows={4}
                placeholder="Please provide a reason for rejecting this invoice..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApprovalManagement;
